import React from 'react';
import { AlertOctagon, Check } from 'lucide-react';
import { useOrgStatus, useOrgStatusSync, useCurrentOrgContext } from '@redbax/shared';

const statusLabel: Record<string, string> = {
  disconnected: 'Reconnecting…',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  connected: 'Listening for reactivation…',
};

const REACTIVATED_AUTO_CLOSE_MS = 6000;
const FADE_MS = 300;

type Phase = 'hidden' | 'suspended' | 'reactivated';

/**
 * Full-viewport blocking modal shown when the current organization is suspended,
 * with animated transitions:
 *  - fades in when a suspension takes effect;
 *  - on reactivation, the red stop sign crossfades to a popping green tick, the
 *    heading switches to "Organization reactivated", and the modal auto-dismisses
 *    after a few seconds (or immediately via Close).
 *
 * A local phase machine keeps the modal mounted through the reactivation animation,
 * even though `isSuspended` has already flipped false. Also drives the live SignalR
 * sync (useOrgStatusSync) so both transitions are received in real time.
 */
export function OrgSuspendedModal() {
  const { connectionStatus, suspensionReason } = useOrgStatusSync();
  const { isSuspended } = useOrgStatus();
  const { currentOrg } = useCurrentOrgContext();

  const [phase, setPhase] = React.useState<Phase>(isSuspended ? 'suspended' : 'hidden');
  const [entered, setEntered] = React.useState(false);
  const wasSuspended = React.useRef(isSuspended);

  // React to suspend/reactivate transitions.
  React.useEffect(() => {
    const was = wasSuspended.current;
    wasSuspended.current = isSuspended;

    if (isSuspended) {
      setPhase('suspended');
    } else if (was) {
      setPhase('reactivated');
    }
  }, [isSuspended]);

  // Fade in on the next frame whenever the modal becomes visible.
  React.useEffect(() => {
    if (phase === 'hidden') {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [phase]);

  const close = React.useCallback(() => {
    setEntered(false);
    window.setTimeout(() => setPhase('hidden'), FADE_MS);
  }, []);

  // Auto-dismiss the success state.
  React.useEffect(() => {
    if (phase !== 'reactivated') return;
    const id = window.setTimeout(close, REACTIVATED_AUTO_CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [phase, close]);

  if (phase === 'hidden') return null;

  const reactivated = phase === 'reactivated';
  const orgName = currentOrg?.name ?? 'Your organization';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="org-suspended-title"
      className={`fixed inset-0 z-50 flex items-center justify-center bg-background/85 p-4 backdrop-blur-sm transition-opacity duration-300 ${
        entered ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <style>{`@keyframes orgCheckPop {
        0% { opacity: 0; transform: scale(0.3); }
        60% { opacity: 1; transform: scale(1.15); }
        100% { opacity: 1; transform: scale(1); }
      }`}</style>

      <div
        className={`w-full max-w-md rounded-xl border bg-surface p-8 text-center shadow-2xl transition-all duration-300 ${
          entered ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        } ${reactivated ? 'border-green-ok/40' : 'border-red-accent/40'}`}
      >
        {/* Icon: red stop sign crossfades to a popping green tick */}
        <div className="relative mx-auto mb-5 h-14 w-14">
          <div
            className={`absolute inset-0 flex items-center justify-center rounded-full bg-red-subtle transition-all duration-300 ${
              reactivated ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
            }`}
          >
            <AlertOctagon className="h-7 w-7 text-red-accent" aria-hidden="true" />
          </div>
          <div
            className="absolute inset-0 flex items-center justify-center rounded-full bg-green-ok/20"
            style={reactivated ? { animation: 'orgCheckPop 500ms ease-out both' } : { opacity: 0, transform: 'scale(0.5)' }}
          >
            <Check className="h-7 w-7 text-green-ok" strokeWidth={3} aria-hidden="true" />
          </div>
        </div>

        <h1 id="org-suspended-title" className="mb-2 text-2xl font-semibold text-text-primary">
          {reactivated ? 'Organization reactivated' : 'Organization suspended'}
        </h1>
        <p className="mb-1 text-sm font-medium text-text-primary">{orgName}</p>

        {reactivated ? (
          <p className="mb-6 text-sm text-text-secondary">
            Access has been restored. You can continue where you left off.
          </p>
        ) : (
          <>
            <p className="mb-6 text-sm text-text-secondary">
              Access to this workspace is locked while your organization is suspended.
              Contact your administrator or Redbax support to restore access.
            </p>

            {suspensionReason && (
              <div className="mb-6 rounded-lg border border-border bg-surface-raised px-4 py-3 text-left text-sm text-text-secondary">
                <span className="font-medium text-text-primary">Reason: </span>
                {suspensionReason}
              </div>
            )}

            <div className="mb-6 flex items-center justify-center gap-2 text-xs text-text-secondary">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  connectionStatus === 'connected'
                    ? 'bg-green-500'
                    : connectionStatus === 'connecting' || connectionStatus === 'reconnecting'
                      ? 'bg-yellow-500'
                      : 'bg-text-secondary'
                }`}
              />
              {statusLabel[connectionStatus] ?? 'Listening for reactivation…'}
            </div>
          </>
        )}

        {reactivated ? (
          <button
            onClick={close}
            className="w-full rounded-lg bg-green-ok px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-green-ok/90"
          >
            Close
          </button>
        ) : (
          <button
            onClick={() => window.location.assign('/')}
            className="w-full rounded-lg bg-red-accent px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-red-hover"
          >
            Check again
          </button>
        )}
      </div>
    </div>
  );
}
